import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Box,
  BoxProps,
  HStack,
  IconButton,
  Spacer,
  Tag,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import { ChevronDownIcon, ChevronUpIcon } from '@/imports/ui/chakra/feather';
import { Carousel } from '@/imports/ui/components/Carousel';

import { AnnouncementsDisplay_AnnouncementFragment } from './graphql-types/AnnouncementsDisplay_AnnouncementFragment';
import {
  AnnouncementsDisplay_MarkAnnouncementsAsRead,
  AnnouncementsDisplay_MarkAnnouncementsAsReadVariables,
} from './graphql-types/AnnouncementsDisplay_MarkAnnouncementsAsRead';

import { Announcement } from './components/Announcement';

const ANNOUNCEMENTS_MUTATION = gql`
  mutation AnnouncementsDisplay_MarkAnnouncementsAsRead(
    $input: MarkAnnouncementsAsReadInput!
  ) {
    markAnnouncementsAsRead(input: $input) {
      data {
        _id
        currentUserShouldSeeAsUnread
      }
    }
  }
`;

export const AnnouncementsDisplay: React.FC<
  {
    announcements: AnnouncementsDisplay_AnnouncementFragment[];
    groupId?: string;
    loadMore: () => void;
    hasMore: boolean;
  } & BoxProps
> & { announcementsFragment: DocumentNode } = ({
  announcements,
  groupId,
  loadMore,
  hasMore,
  ...boxProps
}) => {
  const [markAnnouncementsAsRead] = useMutation<
    AnnouncementsDisplay_MarkAnnouncementsAsRead,
    AnnouncementsDisplay_MarkAnnouncementsAsReadVariables
  >(ANNOUNCEMENTS_MUTATION);
  const numUnread = announcements.filter(
    (announcement) => announcement.currentUserShouldSeeAsUnread
  ).length;
  const announcementsDropdown = useDisclosure();
  const [lastUnreadCount, setLastUnreadCount] = React.useState(numUnread);

  React.useEffect(() => {
    if (numUnread > 0) {
      announcementsDropdown.onOpen();
      setLastUnreadCount(numUnread);
    }
  }, [announcementsDropdown, numUnread]);

  const container = React.useRef<HTMLElement>(null);
  const displaySentinel = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onSentinelIntersection = (entries: any[]): void => {
      entries.forEach((entry: any) => {
        const markAsRead = async (): Promise<void> => {
          await markAnnouncementsAsRead({
            variables: { input: { groupId } },
          });
        };
        if (entry.isIntersecting && numUnread > 0) {
          markAsRead().catch((e) => logError(e));
        }
      });
    };

    const observer = new IntersectionObserver(onSentinelIntersection, {
      root: container.current,
    });
    if (displaySentinel.current) {
      observer.observe(displaySentinel.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [groupId, markAnnouncementsAsRead, numUnread]);

  return (
    <Card p="4" {...boxProps} ref={displaySentinel}>
      <HStack spacing="2">
        <Text textStyle="compactStrong" color="gray.600">
          ANNOUNCEMENTS
        </Text>
        {lastUnreadCount > 0 && (
          <Tag variant="solid" colorScheme="red" borderRadius="xl" size="sm">
            {lastUnreadCount < 10 ? lastUnreadCount : '10+'}
          </Tag>
        )}
        <Spacer />
        <IconButton
          variant="ghost"
          aria-label="Expand Announcements"
          icon={
            announcementsDropdown.isOpen ? (
              <ChevronUpIcon />
            ) : (
              <ChevronDownIcon />
            )
          }
          size="xs"
          colorScheme="gray"
          onClick={announcementsDropdown.onToggle}
        />
      </HStack>
      {announcementsDropdown.isOpen && (
        <Box pt="4">
          <Carousel
            partialVisible={announcements.length > 2 || hasMore}
            responsive={{
              desktop: {
                breakpoint: { max: 3000, min: 1024 },
                items: 2,
                partialVisibilityGutter: 38,
              },
              mobile: {
                breakpoint: { max: 1023, min: 0 },
                items: 1,
                partialVisibilityGutter: 111,
              },
            }}
          >
            {announcements.map((announcement, i) => {
              if (!announcement.postV2) {
                return null;
              }
              const isSecondToLast = i === announcements.length - 2;
              return (
                <Announcement
                  key={announcement._id}
                  announcement={announcement}
                  isSecondToLast={isSecondToLast}
                  loadMore={loadMore}
                  hasMore={hasMore}
                />
              );
            })}
          </Carousel>
        </Box>
      )}
    </Card>
  );
};

AnnouncementsDisplay.announcementsFragment = gql`
  fragment AnnouncementsDisplay_AnnouncementFragment on Announcement {
    _id
    ...Announcement_AnnouncementFragment
  }
  ${Announcement.announcementsFragment}
`;
